/**
 * Action sheets and confirmations as promises, so a click handler reads top to bottom:
 *
 *   const choice = await actionSheet({ title: 'Invoice #182', actions: [
 *       { id: 'send', label: 'Send reminder', icon: 'fa-regular fa-paper-plane' },
 *       { id: 'delete', label: 'Delete invoice', role: 'destructive' },
 *   ] })                                       // 'send' | 'delete' | null (cancelled)
 *
 *   if (await confirmSheet({ title: 'Delete this deal?', confirmLabel: 'Delete deal', destructive: true, hold: true })) …
 *
 * One ActionSheetHost renders them (mounted in App.vue and the dev roots; if none is mounted, one mounts
 * itself). Phones get iOS-style grouped buttons on the modal layer (above any sheet); ≥ 640px gets a compact
 * centred dialog. Requests queue: a second one shows after the first closes.
 */
import { computed, createApp, shallowReactive, type ComputedRef } from 'vue'

export interface SheetAction {
    id: string
    label: string
    /** Font Awesome classes, e.g. 'fa-regular fa-trash'. */
    icon?: string
    role?: 'destructive' | 'default'
    disabled?: boolean
}

export interface ActionSheetOptions {
    title?: string
    message?: string
    actions: SheetAction[]
    /** Default 'Cancel'. */
    cancelLabel?: string
}

export interface ConfirmSheetOptions {
    title: string
    message?: string
    confirmLabel: string
    /** Default 'Cancel'. */
    cancelLabel?: string
    /** Red confirm button and a warning haptic when it opens. */
    destructive?: boolean
    /** Press and hold the confirm button for 600ms (a fill shows progress) instead of a tap. */
    hold?: boolean
}

export type SheetRequest =
    | { id: number; kind: 'actions'; options: ActionSheetOptions; resolve: (value: string | null) => void }
    | { id: number; kind: 'confirm'; options: ConfirmSheetOptions; resolve: (value: boolean) => void }

/** Open and queued requests; the host shows the first. */
export const sheetRequests = shallowReactive<SheetRequest[]>([])

let seq = 0
const hosts = shallowReactive<number[]>([])
let hostSeq = 0
let fallbackPending = false

/** Show an action sheet. Resolves with the chosen action's id, or null when cancelled. */
export function actionSheet(options: ActionSheetOptions): Promise<string | null> {
    return new Promise((resolve) => {
        sheetRequests.push({ id: ++seq, kind: 'actions', options, resolve })
        ensureHost()
    })
}

/** Ask for confirmation. Resolves true when confirmed, false when cancelled. */
export function confirmSheet(options: ConfirmSheetOptions): Promise<boolean> {
    return new Promise((resolve) => {
        sheetRequests.push({ id: ++seq, kind: 'confirm', options, resolve })
        ensureHost()
    })
}

/** Called by ActionSheetHost. Only the first mounted host renders, so a second mount never doubles a sheet. */
export function registerActionSheetHost(): { active: ComputedRef<boolean>; dispose: () => void } {
    const id = ++hostSeq
    hosts.push(id)
    return {
        active: computed(() => hosts[0] === id),
        dispose: () => {
            const at = hosts.indexOf(id)
            if (at >= 0) hosts.splice(at, 1)
        },
    }
}

/** Remove a finished request from the queue (the host calls this once its exit animation ends). */
export function retireSheetRequest(req: SheetRequest): void {
    const at = sheetRequests.indexOf(req)
    if (at >= 0) sheetRequests.splice(at, 1)
}

function ensureHost() {
    if (hosts.length || fallbackPending || typeof document === 'undefined') return
    fallbackPending = true
    // A page without App.vue (a preview, a test page): mount a host of our own.
    queueMicrotask(async () => {
        try {
            if (hosts.length) return
            const { default: Host } = await import('./ActionSheetHost.vue')
            if (hosts.length) return
            const el = document.createElement('div')
            el.setAttribute('data-action-sheet-host', '')
            document.body.appendChild(el)
            createApp(Host).mount(el)
        } finally {
            fallbackPending = false
        }
    })
}
